// Parcours « Configurer mon piano » : choisir comment l'application écoute (micro intégré ou
// piano MIDI, selon ce que le navigateur permet vraiment), demander l'autorisation sur un geste
// explicite, montrer que du son arrive et que des notes sont reconnues, guider un court essai
// (notes isolées puis répétées), expliquer les problèmes rencontrés, puis lancer un exercice
// compatible avec la source choisie.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { InputEvent, InputKind } from '../../engine/types';
import { inputManager } from '../../input/manager';
import type { InputStatus } from '../../input/types';
import { noteName, octaveOf, type NamingSystem } from '../../music/pitchNames';
import { compatibleScores, describeSoundCheck, explainStatus, MIC_LIMIT_SHORT, MIC_LIMIT_TEXT, type CheckTone, type Compatibility, type Explanation } from '../../setup/diagnostics';
import { capabilitiesFor, detectEnvironment, type Availability, type Capabilities } from '../../setup/platform';
import { applyEvent, createTrial, ISOLATED_TARGET, progressOf, REPEATED_TARGET, reportOf, type TrialReport, type TrialState } from '../../setup/trial';
import { settingsStore } from '../../store/settings';
import { MiniKeyboard } from '../components/MiniKeyboard';
import { useInputStatus, useSettings } from '../hooks/stores';
import { activateInput, INPUT_LABELS } from '../inputControl';
import { href } from '../router';

export type SetupStep = 'choose' | 'enable' | 'listen' | 'trial' | 'play';

const STEPS: { id: SetupStep; label: string; title: string }[] = [
  { id: 'choose', label: 'Source', title: 'Comment l’application écoute votre piano' },
  { id: 'enable', label: 'Autorisation', title: 'Autoriser l’écoute' },
  { id: 'listen', label: 'Écoute', title: 'Vérifier que le son arrive' },
  { id: 'trial', label: 'Essai', title: 'Court essai de notes' },
  { id: 'play', label: 'Jouer', title: 'Choisir un exercice' },
];

interface Shown {
  id: number;
  pitch: number | null;
  confidence: 'sure' | 'uncertain' | 'none';
}

let shownCounter = 0;

export function describePitch(pitch: number, naming: NamingSystem): string {
  const octave = naming === 'fr' ? octaveOf(pitch) - 1 : octaveOf(pitch);
  return `${noteName(pitch, naming)}${octave}`;
}

function describeShown(s: Shown, naming: NamingSystem): string {
  if (s.pitch === null) return 'attaque sans hauteur reconnue';
  return s.confidence === 'sure' ? describePitch(s.pitch, naming) : `${describePitch(s.pitch, naming)} ?`;
}

function usesMic(kind: InputKind): boolean {
  return kind === 'mic' || kind === 'synth';
}

export function SetupPage() {
  const settings = useSettings();
  const status = useInputStatus();
  const caps = useMemo<Capabilities>(() => capabilitiesFor(detectEnvironment()), []);
  const [step, setStep] = useState<SetupStep>('choose');
  const [choice, setChoice] = useState<InputKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState<TrialState>(createTrial);
  const [recent, setRecent] = useState<Shown[]>([]);
  const [heard, setHeard] = useState(false);
  const [now, setNow] = useState(() => performance.now());
  const [stepStartedAt, setStepStartedAt] = useState(() => performance.now());
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const stepRef = useRef(step);
  stepRef.current = step;
  const savedRef = useRef(false);

  const goTo = useCallback((s: SetupStep) => {
    setStep(s);
    setStepStartedAt(performance.now());
    setLastEventAt(null);
  }, []);

  // Événements d'entrée : dernières détections affichées et progression de l'essai.
  useEffect(
    () =>
      inputManager.subscribe((e: InputEvent) => {
        if (e.type !== 'noteOn' && e.type !== 'onset') return;
        const shown: Shown = e.type === 'noteOn' ? { id: ++shownCounter, pitch: e.pitch, confidence: e.confidence } : { id: ++shownCounter, pitch: null, confidence: 'none' };
        setRecent((list) => [...list.slice(-5), shown]);
        setLastEventAt(performance.now());
        if (!usesMic(e.source)) setHeard(true);
        if (stepRef.current === 'trial') setTrial((t) => applyEvent(t, e));
      }),
    [],
  );

  // Niveau du micro : « du son arrive » dès qu'il dépasse nettement le bruit de fond.
  const level = status.level;
  useEffect(() => {
    if (level && level.db >= Math.max(-50, level.noise + 12)) setHeard(true);
  }, [level]);

  // Horloge lente pour les indices d'inactivité.
  useEffect(() => {
    if (step === 'choose' || step === 'play') return;
    const id = window.setInterval(() => setNow(performance.now()), 500);
    return () => window.clearInterval(id);
  }, [step]);

  // Autorisation obtenue (micro actif, ou piano MIDI détecté) : on passe à l'écoute sans clic supplémentaire.
  const enabled = choice !== null && status.kind === choice && status.state === 'active' && (choice !== 'midi' || !!status.activeDevice);
  useEffect(() => {
    if (step === 'enable' && enabled) goTo('listen');
  }, [step, enabled, goTo]);

  // Essai terminé : la source validée est mémorisée pour les exercices.
  const report = useMemo(() => reportOf(trial), [trial]);
  useEffect(() => {
    if (trial.phase !== 'done' || savedRef.current || !choice) return;
    savedRef.current = true;
    settingsStore.set({ setupSource: choice, setupDoneAt: new Date().toISOString(), setupVerdict: report.verdict === 'ok' ? 'ok' : 'uncertain' });
  }, [trial.phase, choice, report.verdict]);

  const resetTrial = () => {
    setTrial(createTrial());
    savedRef.current = false;
    setStepStartedAt(performance.now());
    setLastEventAt(null);
  };

  const choose = async (kind: InputKind) => {
    setChoice(kind);
    setTrial(createTrial());
    savedRef.current = false;
    setRecent([]);
    setHeard(false);
    if (kind === 'keyboard') {
      setBusy(true);
      try {
        await activateInput('keyboard');
      } finally {
        setBusy(false);
      }
      goTo('listen');
    } else {
      goTo('enable');
    }
  };

  const enable = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      await activateInput(choice);
    } finally {
      setBusy(false);
    }
  };

  const explanation = choice && status.kind === choice ? explainStatus(status, caps) : null;
  const idleMs = now - (lastEventAt ?? stepStartedAt);
  const active = choice !== null && status.kind === choice && status.state === 'active';
  const noisy = !!level && level.noise > -40;
  const listenCheck = describeSoundCheck({
    kind: choice ?? 'mic',
    active,
    heard,
    noisy,
    sure: recent.filter((r) => r.confidence === 'sure').length,
    uncertain: recent.filter((r) => r.confidence !== 'sure').length,
    idleMs,
  });
  const trialCheck = describeSoundCheck({ kind: choice ?? 'mic', active, heard, noisy, sure: trial.sure, uncertain: trial.uncertain + trial.onsets, idleMs });
  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const current = STEPS[stepIndex];

  return (
    <main className="page setup">
      <header className="page-head">
        <p className="eyebrow">Configurer mon piano</p>
        <h1>{current.title}</h1>
        <ol className="setup-steps" aria-label="Étapes">
          {STEPS.map((s, i) => (
            <li key={s.id} className={s.id === step ? 'on' : i < stepIndex ? 'done' : ''} aria-current={s.id === step ? 'step' : undefined}>
              <span className="step-num" aria-hidden="true">
                {i + 1}
              </span>
              <span className="step-label">{s.label}</span>
            </li>
          ))}
        </ol>
      </header>

      {step === 'choose' && <ChooseStep caps={caps} developer={settings.developer} busy={busy} current={inputManager.currentKind} onChoose={choose} />}
      {step === 'enable' && choice && <EnableStep choice={choice} caps={caps} status={status} busy={busy} explanation={explanation} onEnable={enable} onBack={() => goTo('choose')} />}
      {step === 'listen' && choice && (
        <ListenStep
          choice={choice}
          status={status}
          recent={recent}
          heard={heard}
          check={listenCheck}
          naming={settings.naming}
          explanation={explanation}
          onRetry={enable}
          onContinue={() => {
            resetTrial();
            goTo('trial');
          }}
          onBack={() => goTo('choose')}
        />
      )}
      {step === 'trial' && choice && (
        <TrialStep
          choice={choice}
          trial={trial}
          report={report}
          naming={settings.naming}
          check={trialCheck}
          explanation={explanation}
          onRetry={enable}
          onRestart={resetTrial}
          onContinue={() => goTo('play')}
          onBack={() => goTo('choose')}
        />
      )}
      {step === 'play' && choice && <PlayStep choice={choice} report={report} onBack={() => goTo('choose')} />}
    </main>
  );
}

function ChooseStep({ caps, developer, busy, current, onChoose }: { caps: Capabilities; developer: boolean; busy: boolean; current: InputKind | null; onChoose: (kind: InputKind) => Promise<void> }) {
  const always: Availability = { available: true, reason: null };
  const options: { kind: InputKind; title: string; text: string; availability: Availability; note?: string }[] = [
    {
      kind: 'mic',
      title: 'Micro intégré',
      text: 'Pour un piano acoustique ou numérique, sans câble : l’appareil écoute et reconnaît les notes jouées une à une.',
      availability: caps.mic,
      note: MIC_LIMIT_SHORT,
    },
    {
      kind: 'midi',
      title: 'Piano connecté en MIDI',
      text: 'Piano numérique relié par un câble USB (prise « USB to Host ») ou en Bluetooth MIDI. Reconnaissance exacte, accords compris.',
      availability: caps.midi,
    },
    {
      kind: 'keyboard',
      title: 'Sans piano pour l’instant',
      text: 'Clavier d’essai : les touches de l’ordinateur ou un clavier à l’écran, pour découvrir l’application. Ne valide pas les exercices.',
      availability: always,
    },
  ];
  if (developer) {
    options.push({ kind: 'synth', title: 'Synthèse (test)', text: 'Sons de piano synthétisés injectés dans l’analyse du micro, pour vérifier la chaîne sans instrument.', availability: caps.mic.available ? always : caps.mic });
  }
  return (
    <section className="setup-body">
      <p className="lede">
        Sur cet appareil ({caps.deviceLabel}, {caps.browserLabel}) : micro {caps.mic.available ? 'disponible' : 'indisponible'}, MIDI {caps.midi.available ? 'disponible' : 'indisponible'}.
      </p>
      <details className="details">
        <summary>Et mes données ?</summary>
        <p>Rien n’est enregistré ni envoyé : le son est analysé sur votre appareil. Vos réglages et votre progression restent dans ce navigateur.</p>
      </details>
      <div className="choice-grid">
        {options.map((o) => (
          <button key={o.kind} type="button" className={current === o.kind ? 'choice-card current' : 'choice-card'} disabled={!o.availability.available || busy} onClick={() => void onChoose(o.kind)}>
            <span className="choice-title">{o.title}</span>
            <span className="choice-text">{o.text}</span>
            {o.note && <span className="choice-note">{o.note}</span>}
            <span className={o.availability.available ? 'choice-avail ok' : 'choice-avail no'}>{o.availability.available ? (current === o.kind ? 'Déjà active sur cet appareil' : 'Disponible') : o.availability.reason}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Trouble({ explanation, onRetry }: { explanation: Explanation; onRetry?: () => void }) {
  return (
    <div className="trouble" role="alert">
      <h2>{explanation.title}</h2>
      <p>{explanation.detail}</p>
      {explanation.steps.length > 0 && (
        <ol>
          {explanation.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
      <div className="btn-row">
        {onRetry && (
          <button type="button" className="btn" onClick={onRetry}>
            Réessayer
          </button>
        )}
        <a className="btn ghost" href={href('aide')}>
          Voir l’aide
        </a>
      </div>
    </div>
  );
}

function EnableStep({ choice, caps, status, busy, explanation, onEnable, onBack }: { choice: InputKind; caps: Capabilities; status: InputStatus; busy: boolean; explanation: Explanation | null; onEnable: () => Promise<void>; onBack: () => void }) {
  const midi = choice === 'midi';
  const starting = status.kind === choice && status.state === 'starting';
  return (
    <section className="setup-body">
      {midi ? (
        <p className="lede">Allumez le piano et branchez le câble USB (prise « USB to Host » du piano) avant de continuer. Le navigateur demandera l’autorisation d’accéder aux périphériques MIDI : acceptez.</p>
      ) : (
        <p className="lede">
          Le navigateur va demander l’autorisation d’utiliser le micro. Touchez « Activer le micro », puis « Autoriser ». Rien n’est enregistré ni envoyé : le son est analysé sur votre appareil.
        </p>
      )}
      {!midi && caps.os === 'ios' && (
        <p className="muted">Sur iPhone et iPad, gardez cette page au premier plan pendant l’essai : un appel, le verrouillage de l’écran ou une autre application audio suspend le micro.</p>
      )}
      <div className="btn-row">
        <button type="button" className="btn primary big" onClick={() => void onEnable()} disabled={busy || starting}>
          {starting ? 'Demande en cours…' : midi ? 'Connecter le piano MIDI' : 'Activer le micro'}
        </button>
        <button type="button" className="btn ghost" onClick={onBack}>
          Changer de source
        </button>
      </div>
      {midi && status.kind === 'midi' && status.state === 'active' && status.devices.length > 0 && (
        <p className="muted">Périphériques MIDI vus par le navigateur : {status.devices.map((d) => d.name).join(', ')}. La liste se met à jour quand vous branchez ou débranchez le piano.</p>
      )}
      {explanation && <Trouble explanation={explanation} onRetry={() => void onEnable()} />}
    </section>
  );
}

function SoundMeter({ level, heard }: { level: InputStatus['level']; heard: boolean }) {
  const db = level?.db ?? -100;
  const noise = level?.noise ?? -100;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v + 60) / 60) * 100));
  return (
    <div className="sound-meter" role="img" aria-label={`Niveau du micro : ${Math.round(db)} décibels`}>
      <div className="sound-bar">
        <span className="sound-fill" style={{ width: `${pct(db)}%` }} />
        <span className="sound-noise" style={{ left: `${pct(noise)}%` }} title="Bruit de fond" />
      </div>
      <div className="sound-legend">
        <span>silence</span>
        <span className={heard ? 'ok' : ''}>{heard ? 'Son reçu ✓' : 'Aucun son reçu pour l’instant'}</span>
        <span>fort</span>
      </div>
    </div>
  );
}

function ListenStep({
  choice,
  status,
  recent,
  heard,
  check,
  naming,
  explanation,
  onRetry,
  onContinue,
  onBack,
}: {
  choice: InputKind;
  status: InputStatus;
  recent: Shown[];
  heard: boolean;
  check: { tone: CheckTone; text: string };
  naming: NamingSystem;
  explanation: Explanation | null;
  onRetry: () => Promise<void>;
  onContinue: () => void;
  onBack: () => void;
}) {
  const mic = usesMic(choice);
  const last = recent[recent.length - 1];
  const instruction = mic
    ? 'Jouez une note sur votre piano, puis une autre. Le niveau du micro et la note reconnue s’affichent en direct.'
    : choice === 'midi'
      ? 'Appuyez sur quelques touches du piano : chaque note doit apparaître immédiatement.'
      : 'Appuyez sur une touche de la rangée du milieu du clavier de l’ordinateur (Q S D F G H J K en AZERTY) ou sur le clavier ci-dessous.';
  return (
    <section className="setup-body">
      <p className="lede">{instruction}</p>
      {mic && <SoundMeter level={status.level} heard={heard} />}
      <div className={`big-note ${last?.confidence === 'sure' ? 'sure' : 'unsure'}`} aria-live="polite" aria-label="Dernière note reconnue">
        {last ? describeShown(last, naming) : '—'}
      </div>
      {recent.length > 1 && (
        <p className="muted recent-notes">
          Dernières détections :{' '}
          {recent
            .slice()
            .reverse()
            .map((r) => describeShown(r, naming))
            .join(' · ')}
        </p>
      )}
      <p className={`check-line ${check.tone}`} aria-live="polite">
        {check.text}
      </p>
      {(choice === 'keyboard' || choice === 'synth') && <MiniKeyboard />}
      {explanation && <Trouble explanation={explanation} onRetry={() => void onRetry()} />}
      <div className="btn-row">
        <button type="button" className="btn primary big" onClick={onContinue} disabled={recent.length === 0}>
          Passer à l’essai
        </button>
        <button type="button" className="btn ghost" onClick={onBack}>
          Changer de source
        </button>
      </div>
      {recent.length === 0 && <p className="muted">Le bouton s’active dès qu’une première note est reconnue.</p>}
    </section>
  );
}

function TrialStep({
  choice,
  trial,
  report,
  naming,
  check,
  explanation,
  onRetry,
  onRestart,
  onContinue,
  onBack,
}: {
  choice: InputKind;
  trial: TrialState;
  report: TrialReport;
  naming: NamingSystem;
  check: { tone: CheckTone; text: string };
  explanation: Explanation | null;
  onRetry: () => Promise<void>;
  onRestart: () => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const progress = progressOf(trial);
  const instruction =
    trial.phase === 'isolated'
      ? `Jouez ${ISOLATED_TARGET} notes différentes, une par une, en laissant sonner chaque note une seconde.`
      : `Maintenant, jouez ${REPEATED_TARGET} fois de suite la même note, à un rythme tranquille, environ une par seconde.`;
  return (
    <section className="setup-body">
      <ol className="trial-phases" aria-label="Étapes de l’essai">
        <li className={trial.phase === 'isolated' ? 'on' : 'done'}>1. Notes isolées</li>
        <li className={trial.phase === 'repeated' ? 'on' : trial.phase === 'done' ? 'done' : ''}>2. Note répétée</li>
      </ol>
      {trial.phase !== 'done' ? (
        <>
          <p className="lede">{instruction}</p>
          <div className="trial-progress" role="progressbar" aria-valuemin={0} aria-valuemax={progress.target} aria-valuenow={progress.done} aria-label="Progression de l’essai">
            {Array.from({ length: progress.target }, (_, i) => (
              <span key={i} className={i < progress.done ? 'dot on' : 'dot'} />
            ))}
            <span className="trial-count">
              {progress.done} / {progress.target}
            </span>
          </div>
          {trial.phase === 'isolated' && trial.isolated.length > 0 && <p className="muted">Reconnues : {trial.isolated.map((p) => describePitch(p, naming)).join(', ')}</p>}
          {trial.phase === 'repeated' && trial.repeated.pitch !== null && (
            <p className="muted">
              Note répétée : {describePitch(trial.repeated.pitch, naming)} × {trial.repeated.count}
            </p>
          )}
          {trial.last && trial.last.confidence !== 'sure' && (
            <p className="check-line warn">
              {trial.last.pitch === null ? 'Attaque entendue sans hauteur reconnue.' : `${describePitch(trial.last.pitch, naming)} reconnue avec doute.`} Une détection incertaine ne compte pas : jouez une seule note, franche, sans pédale.
            </p>
          )}
          <p className={`check-line ${check.tone}`} aria-live="polite">
            {check.text}
          </p>
          {(choice === 'keyboard' || choice === 'synth') && <MiniKeyboard />}
          {explanation && <Trouble explanation={explanation} onRetry={() => void onRetry()} />}
          <div className="btn-row">
            <button type="button" className="btn ghost" onClick={onRestart}>
              Recommencer l’essai
            </button>
            <button type="button" className="btn ghost" onClick={onBack}>
              Changer de source
            </button>
          </div>
        </>
      ) : (
        <Verdict choice={choice} report={report} onRestart={onRestart} onContinue={onContinue} />
      )}
    </section>
  );
}

function Verdict({ choice, report, onRestart, onContinue }: { choice: InputKind; report: TrialReport; onRestart: () => void; onContinue: () => void }) {
  const ok = report.verdict === 'ok';
  const mic = usesMic(choice);
  const instrument = choice === 'mic' || choice === 'midi';
  const title = !ok ? 'Reconnaissance incertaine' : choice === 'mic' ? 'Votre piano est prêt' : choice === 'midi' ? 'Votre piano MIDI est prêt' : choice === 'keyboard' ? 'Le clavier d’essai fonctionne' : 'Chaîne audio simulée vérifiée';
  const counts = `${report.sure} notes reconnues avec certitude${report.uncertain > 0 ? `, ${report.uncertain} avec doute` : ''}.`;
  const text = !ok
    ? 'L’essai est terminé, mais une partie des notes n’a pas été reconnue avec certitude. Vous pouvez jouer quand même : une note douteuse n’est jamais comptée comme juste, elle est signalée à part dans le bilan.'
    : choice === 'keyboard'
      ? `${counts} Vous pouvez découvrir les exercices et gagner les récompenses de découverte. Pour valider vos réussites, configurez votre vrai piano (micro ou MIDI) quand il sera là.`
      : choice === 'synth'
        ? `${counts} La chaîne audio (contexte, analyse, détection) fonctionne avec des sons simulés. Le micro réel reste à essayer sur votre piano.`
        : `${counts} La source « ${INPUT_LABELS[choice]} » est enregistrée pour vos exercices.`;
  return (
    <div className={!ok ? 'verdict warn' : instrument ? 'verdict ok' : 'verdict neutral'} role="status">
      <h2>{title}</h2>
      <p>{text}</p>
      {!ok && (
        <ul>
          {report.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      {!ok && mic && (
        <ul className="tips">
          <li>Rapprochez l’appareil du piano (moins d’un mètre) et réduisez le bruit (fenêtres, télévision).</li>
          <li>Jouez une seule note à la fois, franche, sans pédale ; laissez-la sonner une seconde.</li>
          <li>Évitez un casque ou une enceinte Bluetooth pendant l’écoute.</li>
          <li>Si votre piano a une prise USB, le MIDI donne une reconnaissance exacte.</li>
        </ul>
      )}
      <div className="btn-row">
        <button type="button" className="btn primary big" onClick={onContinue}>
          Choisir un exercice
        </button>
        <button type="button" className="btn" onClick={onRestart}>
          Recommencer l’essai
        </button>
      </div>
    </div>
  );
}

function PlayStep({ choice, report, onBack }: { choice: InputKind; report: TrialReport; onBack: () => void }) {
  const compat = compatibleScores(choice);
  const playable = compat.filter((c) => c.mode !== 'midiOnly');
  const midiOnly = compat.filter((c) => c.mode === 'midiOnly');
  const first = playable[0];
  const mic = usesMic(choice);
  const labels: Record<Compatibility, string | null> = { ok: null, oneHand: 'une main à la fois', midiOnly: 'MIDI nécessaire' };
  return (
    <section className="setup-body">
      <p className="lede">
        {mic ? 'Contenus adaptés au micro : une note à la fois. Les morceaux à deux mains s’ouvrent avec la main droite.' : choice === 'midi' ? 'Avec le MIDI, tous les exercices et morceaux sont disponibles, accords compris.' : 'Avec le clavier d’essai, tous les contenus se travaillent, sans validation des exercices.'}
      </p>
      {mic && (
        <details className="details">
          <summary>Pourquoi une main à la fois ?</summary>
          <p>{MIC_LIMIT_TEXT}</p>
        </details>
      )}
      {report.verdict === 'uncertain' && <p className="warn-text">Reconnaissance incertaine pendant l’essai : commencez par un exercice court et lent, et surveillez les « détections incertaines » du bilan.</p>}
      {first && (
        <a className="btn primary big" href={first.mode === 'oneHand' ? href('pratique', first.score.id, 'md') : href('pratique', first.score.id)}>
          Lancer « {first.score.title} »
        </a>
      )}
      <ul className="compat-list" aria-label="Contenus compatibles">
        {playable.map((c) => (
          <li key={c.score.id}>
            <a href={c.mode === 'oneHand' ? href('pratique', c.score.id, 'md') : href('pratique', c.score.id)}>{c.score.title}</a>
            {labels[c.mode] && <span className="badge">{labels[c.mode]}</span>}
            <span className="muted">{c.score.description}</span>
          </li>
        ))}
      </ul>
      {midiOnly.length > 0 && (
        <p className="muted">
          Non disponibles avec le micro, car ils contiennent des accords : {midiOnly.map((c) => c.score.title).join(', ')}. Ils demandent un piano connecté en MIDI.
        </p>
      )}
      <div className="btn-row">
        <a className="btn" href={href('catalogue')}>
          Voir tout le catalogue
        </a>
        <button type="button" className="btn ghost" onClick={onBack}>
          Changer de source
        </button>
      </div>
    </section>
  );
}
